import { type ConsumptionEntry as MonthlyEntry } from '../data/consumptionData12Months';
import { type ConsumptionEntry as DailyEntry } from '../data/consumptionDataDaily';
import { loadConsumptionDrax, loadHalfHourlyConsumptionDrax } from '../../../common/api/dataApi';
import { fillEmptyMonths } from '../components/energyusage/types/ChartUtils';
import { toast } from 'react-toastify';

export interface ConsumptionResponse {
  endDate: string;
  entityId: string;
  startDate: string;
  entityType: string;
  consumptions: ConsumptionEntry[];
  groupingInterval: 'Month' | 'Day' | 'HalfHour';
}

export interface HHConsumptionResponse {
  entityId: string;
  entityType: string;
  endTimeDate: string;
  startTimeDate: string;
  consumptions: HHConsumptionData[];
}

export type PeriodOption = '24' | '12' | '6';
export type Granularity = 'monthly' | 'daily' | 'halfhourly';

export interface GetConsumptionParams {
  url?: string;
  context: any;
  period?: string;
  entityId: string;
  startDate: string;
  endDate: string;
  groupingInterval: Api.GroupingInterval;
  entityType?: Api.EntityType;
  customerId?: string;
  requestType: Api.HHRequestType;
}

export interface HalfHourlyParams {
  url?: string;
  context: any;
  entityId: string;
  startDate: string;
  endDate: string;
  entityType?: Api.EntityType;
  customerId?: string;
  requestType: Api.HHRequestType;
}

export type ConsumptionEntry = MonthlyEntry | DailyEntry;

export interface HHConsumptionData {
  date: string;
  mpanCore: string;
  periods: HHPeriod[];
}

export interface HHPeriod {
  period: number;
  periodStart: string;
  consumptionValue: number;
  isEstimated: boolean;
}

export async function getConsumptionData(params: GetConsumptionParams): Promise<ConsumptionResponse> {
  const consumptionResponse = await loadConsumptionDrax(
    process.env.REACT_APP_API_URL,
    params.context,
    params.entityId,
    params.startDate,
    params.endDate,
    params.groupingInterval,
    params.entityType,
    params.requestType,
    params.customerId
  ).then(data => {
    if (!data || data.success === false) {
      toast.error('Consumption unexpected error occurred', { position: 'bottom-right' });
    }
    return data;
  });

  if (params.groupingInterval === 'Month') {
    if (consumptionResponse && consumptionResponse.data && consumptionResponse.data.consumptions) {
      const closestDate = params.endDate;
      const oldestDate = params.startDate;

      consumptionResponse.data.consumptions = fillEmptyMonths(
        closestDate,
        oldestDate,
        (consumptionResponse.data.consumptions as Api.IDraxConsumptionData[]) || []
      );

      consumptionResponse.data.consumptions.sort((a: Api.IDraxConsumptionData, b: Api.IDraxConsumptionData) => {
        if (a.intervalStart > b.intervalStart) {
          return 1;
        }

        if (a.intervalStart < b.intervalStart) {
          return -1;
        }

        return 0;
      });
    }
  }

  if (consumptionResponse) {
    return consumptionResponse.data;
  } else {
    let result: ConsumptionResponse = {
      consumptions: [],
      endDate: params.endDate,
      entityId: params.entityId,
      startDate: params.startDate,
      entityType: params.entityType as string,
      groupingInterval: params.groupingInterval,
    };
    return result;
  }
}

export async function loadHalfHourlyConsumptionData(params: HalfHourlyParams): Promise<HHConsumptionResponse> {
  const consumptionResponse = await loadHalfHourlyConsumptionDrax(
    process.env.REACT_APP_API_URL,
    params.context,
    params.entityId,
    params.startDate,
    params.endDate,
    params.entityType,
    params.customerId as string,
    params.requestType
  );

  await consumptionResponse;

  if (consumptionResponse) {
    return consumptionResponse.data;
  } else {
    let result: HHConsumptionResponse = {
      consumptions: [],
      entityId: params.entityId,
      entityType: params.entityType as string,
      endTimeDate: params.endDate,
      startTimeDate: params.startDate,
    };
    return result;
  }
}
